import React, { useState, useRef, useCallback } from 'react';

interface Segment {
  id: string;
  index: number;
  startTime: number;
  endTime: number;
  /** null when the segment inherits the clip's level; the timeline only displays it. */
  volume: number | null;
  muted?: boolean;
  fadeIn?: number;
  fadeOut?: number;
  speed?: number | null;
  transition?: string;
}

interface TimelineProps {
  segments: Segment[];
  /**
   * Trim mode. The edge handles are what actually trims a segment, so they appear only while
   * the toolbar's Trim is on — otherwise a drag near an edge, meant to reorder, resizes instead.
   */
  trimActive?: boolean;
  duration: number;
  currentTime: number;
  zoom: number;
  onSeek: (time: number) => void;
  /** `additive` is set when Ctrl/Cmd/Shift is held, asking to extend the selection. */
  onSegmentSelect: (index: number, additive: boolean) => void;
  selectedSegment: number | null;
  /** The second of at most two adjacent selected segments. */
  secondarySegment?: number | null;
  onSegmentTrim: (segIndex: number, edge: 'start' | 'end', newTime: number) => void;
  /** Fired once when a trim drag begins, so undo captures one state per gesture. */
  onSegmentTrimStart?: () => void;
  onSegmentReorder: (fromIndex: number, toIndex: number) => void;
  onSegmentDelete: (segIndex: number) => void;
  onSegmentMute: (segIndex: number) => void;
  /** Index of the segment the preview is playing in isolation, if any. */
  playingSegment?: number | null;
  /** Index of the segment the preview is looping, if any. */
  loopingSegment?: number | null;
  onSegmentPlay?: (segIndex: number) => void;
  onSegmentLoop?: (segIndex: number) => void;
  /** `atTime` is where inside the segment the pointer was, so a cut lands under the cursor. */
  onContextMenu: (e: React.MouseEvent, segIndex: number, atTime: number) => void;
}

const MIN_SEGMENT_DURATION = 0.5;

const stamp = (t: number) => {
  const m = Math.floor(Math.max(0, t) / 60);
  const sec = Math.max(0, t) % 60;
  return `${m}:${sec.toFixed(1).padStart(4, '0')}`;
};

const Timeline: React.FC<TimelineProps> = ({
  segments, trimActive = false, duration, currentTime, zoom,
  onSeek, onSegmentSelect, selectedSegment, secondarySegment,
  onSegmentTrim, onSegmentTrimStart, onSegmentReorder, onSegmentDelete, onSegmentMute,
  playingSegment, loopingSegment, onSegmentPlay, onSegmentLoop, onContextMenu,
}) => {
  const [hoveredSeg, setHoveredSeg] = useState<number | null>(null);
  /** Pointer position inside the hovered segment: the cut a Split Here would make. */
  const [hoverPoint, setHoverPoint] = useState<{ segIndex: number; pct: number; time: number } | null>(null);

  /** Pointer x within a segment element, as a fraction and as a clip time. */
  const pointerInSegment = (e: React.MouseEvent, seg: Segment) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pct = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0;
    const clamped = Math.max(0, Math.min(1, pct));
    return { pct: clamped * 100, time: seg.startTime + clamped * (seg.endTime - seg.startTime) };
  };
  const [trimming, setTrimming] = useState<{ segIndex: number; edge: 'start' | 'end'; startX: number; origTime: number } | null>(null);
  const [dragging, setDragging] = useState<{ segIndex: number; startX: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const dropTargetRef = useRef<number | null>(null);
  const segmentsRef = useRef<HTMLDivElement>(null);

  /** Keeps the ref and the rendered indicator in step. */
  const updateDropTarget = useCallback((next: number | null) => {
    dropTargetRef.current = next;
    setDropTarget(next);
  }, []);

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / (rect.width * zoom);
    onSeek(Math.max(0, Math.min(duration, pct * duration)));
  };

  const playheadPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Trim handle mouse down
  const handleTrimStart = useCallback((e: React.MouseEvent, segIndex: number, edge: 'start' | 'end') => {
    e.stopPropagation();
    e.preventDefault();
    setHoverPoint(null);
    const seg = segments[segIndex];
    // A drag before the media reports its length would clamp against a zero duration, writing
    // an end of 0 — below its own start.
    if (!seg || duration <= 0) return;
    const origTime = edge === 'start' ? seg.startTime : seg.endTime;
    setTrimming({ segIndex, edge, startX: e.clientX, origTime });
    onSegmentTrimStart?.();

    const handleMove = (me: MouseEvent) => {
      if (!segmentsRef.current) return;
      const rect = segmentsRef.current.getBoundingClientRect();
      const pxPerSec = (rect.width * zoom) / duration;
      const dx = me.clientX - e.clientX;
      let newTime = origTime + dx / pxPerSec;

      // Snap to 0.5s if shift held
      if (me.shiftKey) newTime = Math.round(newTime * 2) / 2;

      if (edge === 'start') {
        newTime = Math.max(0, Math.min(seg.endTime - MIN_SEGMENT_DURATION, newTime));
      } else {
        newTime = Math.max(seg.startTime + MIN_SEGMENT_DURATION, Math.min(duration, newTime));
      }
      onSegmentTrim(seg.index, edge, newTime);
    };

    const handleUp = () => {
      setTrimming(null);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }, [segments, duration, zoom, onSegmentTrim, onSegmentTrimStart]);

  // Drag reorder mouse down
  const handleDragStart = useCallback((e: React.MouseEvent, segIndex: number) => {
    // Only start drag on body (not trim handles), and never while Trim owns the pointer.
    if (trimActive) return;
    if ((e.target as HTMLElement).classList.contains('segment-trim-handle')) return;
    setDragging({ segIndex, startX: e.clientX });

    const handleMove = (me: MouseEvent) => {
      if (!segmentsRef.current) return;
      // Only the segments count. The drop indicator is a sibling in this container, so walking
      // every child shifts each index by one as soon as an indicator appears — and a drop before
      // the first segment then resolves to the segment being dragged, which is treated as "no
      // move" and silently does nothing.
      const els = segmentsRef.current.querySelectorAll('.timeline-segment');
      let target = segments.length;
      for (let i = 0; i < els.length; i++) {
        const childRect = els[i].getBoundingClientRect();
        if (me.clientX < childRect.left + childRect.width / 2) {
          target = i;
          break;
        }
      }
      updateDropTarget(target);
    };

    const handleUp = () => {
      const target = dropTargetRef.current;
      if (target !== null && target !== segIndex && target !== segIndex + 1) {
        const toIdx = target > segIndex ? target - 1 : target;
        onSegmentReorder(segIndex, toIdx);
      }
      setDragging(null);
      updateDropTarget(null);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }, [segments, trimActive, onSegmentReorder, updateDropTarget]);

  return (
    <div className="timeline-panel" style={{ overflow: 'auto' }}>
      <div className="timeline-segments" ref={segmentsRef} style={{ width: `${100 * zoom}%` }}>
        {segments.map((seg, i) => {
          const widthPct = duration > 0
            ? ((seg.endTime - seg.startTime) / duration) * 100
            : 25;
          const isSelected = selectedSegment === seg.index;
          const isSecondary = secondarySegment === seg.index;
          const isHovered = hoveredSeg === i;
          const isDragSource = dragging?.segIndex === i;
          const segDur = seg.endTime - seg.startTime;
          // The playhead sits in exactly one segment; that one shows a live position.
          const isCurrent = currentTime >= seg.startTime && currentTime < seg.endTime;
          // Where a cut would land if the author asked for one here. Too close to either edge
          // and one half would fall under the minimum, so the line says so rather than lying.
          const point = hoverPoint && hoverPoint.segIndex === i ? hoverPoint : null;
          const cuttable = !!point
            && point.time - seg.startTime >= MIN_SEGMENT_DURATION
            && seg.endTime - point.time >= MIN_SEGMENT_DURATION;

          return (
            <React.Fragment key={seg.id}>
              {/* Drop indicator */}
              {dropTarget === i && dragging && dragging.segIndex !== i && (
                <div className="segment-drop-indicator" />
              )}
              <div
                className={`timeline-segment ${isSelected ? 'timeline-segment--selected' : ''} ${isSecondary ? 'timeline-segment--selected-2' : ''} ${isDragSource ? 'timeline-segment--dragging' : ''}`}
                style={{ width: `${widthPct}%` }}
                onClick={(e) => onSegmentSelect(seg.index, e.ctrlKey || e.metaKey || e.shiftKey)}
                onMouseEnter={() => setHoveredSeg(i)}
                onMouseMove={(e) => setHoverPoint({ segIndex: i, ...pointerInSegment(e, seg) })}
                onMouseLeave={() => { setHoveredSeg(null); setHoverPoint(null); }}
                onMouseDown={(e) => handleDragStart(e, i)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onContextMenu(e, seg.index, pointerInSegment(e, seg).time);
                }}
              >
                {/* Left trim handle */}
                {isHovered && trimActive && (
                  <div
                    className="segment-trim-handle segment-trim-handle--left"
                    onMouseDown={(e) => handleTrimStart(e, i, 'start')}
                  />
                )}

                {point && (
                  <div
                    className={`segment-split-marker ${cuttable ? '' : 'segment-split-marker--blocked'}`}
                    style={{ left: `${point.pct}%` }}
                    title={cuttable ? `Split here — ${stamp(point.time)}` : 'Too close to the edge to split'}
                  >
                    <span className="segment-split-marker-cap">{stamp(point.time)}</span>
                  </div>
                )}

                {(isHovered || playingSegment === seg.index || loopingSegment === seg.index) && (
                  <div className="segment-play-actions">
                    <button
                      className={`segment-hover-btn ${playingSegment === seg.index ? 'segment-hover-btn--on' : ''}`}
                      onClick={(e) => { e.stopPropagation(); onSegmentPlay?.(seg.index); }}
                      title={playingSegment === seg.index ? 'Stop' : 'Play this segment only'}
                    >{playingSegment === seg.index ? '⏸' : '▶'}</button>
                    <button
                      className={`segment-hover-btn ${loopingSegment === seg.index ? 'segment-hover-btn--on' : ''}`}
                      onClick={(e) => { e.stopPropagation(); onSegmentLoop?.(seg.index); }}
                      title={loopingSegment === seg.index ? 'Stop looping' : 'Loop this segment'}
                    >🔁</button>
                  </div>
                )}

                <div className="segment-content">
                  <span className="segment-label">Segment {seg.index + 1}</span>
                  <div className="segment-waveform" />
                  <div className="segment-times">
                    <span className="segment-range">{stamp(seg.startTime)} – {stamp(seg.endTime)}</span>
                    {isCurrent && (
                      <span className="segment-now" title="Playhead position inside this segment">
                        ▸ {stamp(currentTime)}
                      </span>
                    )}
                    <span className="segment-duration">{segDur.toFixed(1)}s</span>
                  </div>
                </div>

                {/* Hover actions */}
                {isHovered && (
                  <div className="segment-hover-actions">
                    <button
                      className="segment-hover-btn segment-hover-btn--delete"
                      onClick={(e) => { e.stopPropagation(); onSegmentDelete(seg.index); }}
                      title="Delete segment"
                    >✕</button>
                    <button
                      className="segment-hover-btn segment-hover-btn--volume"
                      onClick={(e) => { e.stopPropagation(); onSegmentMute(seg.index); }}
                      title={seg.muted ? 'Unmute' : 'Mute'}
                    >{seg.muted ? '🔇' : '🔊'}</button>
                  </div>
                )}

                {/* Transition indicator */}
                {seg.transition && seg.transition !== 'none' && i > 0 && (
                  <div className="segment-transition-badge" title={seg.transition}>
                    ➡️
                  </div>
                )}

                {/* Right trim handle */}
                {isHovered && trimActive && (
                  <div
                    className="segment-trim-handle segment-trim-handle--right"
                    onMouseDown={(e) => handleTrimStart(e, i, 'end')}
                  />
                )}
              </div>
            </React.Fragment>
          );
        })}
        {/* Final drop indicator */}
        {dropTarget === segments.length && dragging && (
          <div className="segment-drop-indicator" />
        )}
      </div>

      <div className="timeline-track" onClick={handleTimelineClick} style={{ width: `${100 * zoom}%` }}>
        <div className="timeline-playhead" style={{ left: `${playheadPct}%` }} />
        <div className="timeline-progress" style={{ width: `${playheadPct}%` }} />
      </div>

      <div className="timeline-ticks" style={{ width: `${100 * zoom}%` }}>
        {Array.from({ length: Math.ceil(duration) + 1 }, (_, i) => (
          <span key={i} className="timeline-tick" style={{ left: `${(i / duration) * 100}%` }}>
            {i % 5 === 0 ? `${Math.floor(i / 60)}:${(i % 60).toString().padStart(2, '0')}` : ''}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Timeline;
